// ФТУЕ.7: клиентский маппер серверных строк на EN.
//
// Сервер хранит часть данных только на русском (title_text_ru, league,
// hero name_ru, card name_ru — статика из движка). Чтобы не делать
// миграцию БД и пересборку всех данных, ставим клиентский слой
// «русское серверное значение → EN-перевод».
//
// Если игрок поставил RU — все функции просто возвращают исходник.

import { tr } from './useTr';

// ────────── Титулы ──────────
// Все title_code из сервера: starting-bundle, BP-rewards, achievements,
// account-rewards.
const TITLE_EN: Record<string, string> = {
  // starting-bundle.ts
  ptashka: 'Rookie',
  // FTUE-бонус (profile.ts)
  iz_uchebki: 'Fresh Out the Boot',
  // BP free-track (seed-battle-pass.ts)
  nishiy: 'Beggar',
  uporniy: 'Persistent',
  king_of_slums: 'King of the Slums',
  // BP paid-track
  season_1_korp: 'Corp of Season 1',
  season_1_top: 'Pinnacle of Season 1',
  season_1_legend: 'Legend of Season 1',
  // Account-level rewards (account-rewards.ts)
  trudyaga: 'Grinder',
  // Achievement rewards (seed-achievements.ts)
  as: 'Ace',
  kollektsioner: 'Collector',
  ne_odin: 'Not Alone',
  korp_agent: 'Corp Agent',
  dzheki: 'Jackie',
  legenda: 'Legend',
  nft: 'NFT',
  // МАР.31: титулы маркета
  perekup: 'Flipper',
  hava_nagila: 'Hava Nagila',
  // Этап 72: подпись Мецената (перк paid БП ур.1) стала выбираемым титулом
  patron: 'Patron'
};
export function trTitleText(code: string | null | undefined, textRu: string): string {
  if (!code) return textRu;
  const en = TITLE_EN[code];
  return en ? tr(textRu, en) : textRu;
}

// ────────── Лиги ──────────
// Серверные значения совпадают с buildLeagues в RatingScreen.
const LEAGUE_EN: Record<string, string> = {
  'Трущобы':       'Slums',
  'Стритраннеры':  'Streetrunners',
  'Нетраннеры':    'Netrunners',
  'Фиксеры':       'Fixers',
  'Оперативники':  'Operatives',
  'Корпораты':     'Corporates',
  'Высшая лига':   'Big League',
  'Высшая Лига':   'Big League'  // alias на случай разных регистров
};
export function trLeague(leagueRu: string | null | undefined): string {
  if (!leagueRu) return '';
  const en = LEAGUE_EN[leagueRu];
  return en ? tr(leagueRu, en) : leagueRu;
}

// ────────── Имена Героев ──────────
// hero.id → EN-имя. Если нет — fallback на h.name_ru.
const HERO_NAME_EN: Record<string, string> = {
  // Deus (4)
  deus_tanaki: 'Tanaki',
  deus_kirosa: 'Kirosa',
  deus_nikosi: 'Nikosi',
  deus_sina: 'Sina',
  // Synthetics (4)
  syn_bishor: '«Butcher» Bishor',
  syn_rast: '«Dirty Tactic» Rust',
  syn_skvill: '«Brainy» Skwill',
  syn_uein: 'Reaper Wayne',
  // Global (4)
  global_iv: 'IV',
  global_li_pul: 'Li Pul',
  global_mit: 'Mit',
  global_rayna: 'Rayna',
  // Liv Life (4)
  livlife_ariana: 'Ariana',
  livlife_klod: 'Claude',
  livlife_lisa: 'Lisa',
  livlife_zayn: 'Zane',
  // Neutral (1) — стартовый Лидер улицы
  neutral_lider_ulicy: 'Street Leader'
};
export function trHeroName(heroId: string | null | undefined, nameRu: string): string {
  if (!heroId) return nameRu;
  const en = HERO_NAME_EN[heroId];
  return en ? tr(nameRu, en) : nameRu;
}

// ────────── Имена карт ──────────
// card.id → EN-имя. Для начала покрываем стартовые / самые видимые
// карты, остальные fallback на name_ru. Полный набор — отдельным
// проходом через xlsx-колонку name_en.
const CARD_NAME_EN: Record<string, string> = {
  // Нейтральные стартовые (из tutorial-бой + первые ШК)
  neutral_kontrabandist_dronov:    'Drone Smuggler',
  neutral_kontrabandist_dronov_action: 'Drone Smuggler (Action)',
  neutral_drone_muha:              'Drone «Fly»',
  neutral_glava_klana:             'Clan Chief',
  neutral_klan_kochevnikov:        'Wasteland Nomads',
  // Stub: остальные карты пока не переведены — fallback на name_ru.
};
export function trCardName(cardId: string | null | undefined, nameRu: string): string {
  if (!cardId) return nameRu;
  const en = CARD_NAME_EN[cardId];
  return en ? tr(nameRu, en) : nameRu;
}

// ────────── Текст способностей карт (EN) ──────────
// Имена карт переведены в данных (card.name_en). Текст способностей — тоже из
// данных: card.ability_text_en (gen-cards генерит из xlsx-колонок *_desc_en).
// Этот хелпер выбирает EN- или RU-версию по текущему языку (частичный перевод:
// блок берём EN, иначе RU). Колонки *_desc_en заполняются в Excel-мастере.
type AbilityBlocksEn = { order?: string; trigger?: string; deathrattle?: string; aura?: string; active?: string };

export function trCardAbility(
  at: AbilityBlocksEn | undefined,
  atEn: AbilityBlocksEn | undefined
): AbilityBlocksEn {
  const ru = at || {};
  if (tr('ru', 'en') !== 'en') return ru; // RU-режим
  const en = atEn || {};
  if (Object.keys(en).length === 0) return ru; // EN-перевода для карты ещё нет
  return {
    order:       en.order       ?? ru.order,
    trigger:     en.trigger     ?? ru.trigger,
    deathrattle: en.deathrattle ?? ru.deathrattle,
    aura:        en.aura        ?? ru.aura,
    active:      en.active      ?? ru.active,
  };
}
